import { Dropdown, Button, Typography, Tag, Tooltip } from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { MoreOutlined, InfoCircleFilled } from '@ant-design/icons';
import { flippoClient } from '@/core';
import { browserFileDownload } from '@/utils';
import { useDropdownMenu } from './hooks/useDropdownMenu';
import { DATE_TIME_FORMAT_MOMENT_DOTS } from '@/platform';
import { confirmation } from '@/components';
import { useDeleteReportMutation } from '@/redux';
import classes from './ReportCard.module.scss';

type PropsType = {
  name: string;
  reference: string;
  createdAt: string;
  id: string;
  isDataSource: boolean;
  metricsDate: string;
};

const ConfirmationBody = () => {
  return (
    <div>
      <Typography.Paragraph>
        The data from this report is used for formation charts and other project statistics.
      </Typography.Paragraph>
      <Typography.Paragraph className={classes.confirmationBodyText}>
        <InfoCircleFilled className={classes.warningIcon} /> If you delete the report, the data for this month
        will not be displayed in the charts.
      </Typography.Paragraph>
      <Typography.Paragraph className="mt-4">
        Are you sure you want to delete this report?
      </Typography.Paragraph>
    </div>
  );
};

export const ReportCard = (props: PropsType) => {
  const { name, reference, id, createdAt, isDataSource, metricsDate } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.project.report' });
  const [deleteReport] = useDeleteReportMutation();

  const onClickMenuExport = async () =>
    browserFileDownload({ blob: await flippoClient.fetch(reference), fileName: name });
  const onClickMenuDelete = () => {
    if (!isDataSource) {
      deleteReport({ id });
      return;
    }

    confirmation.show({
      title: t('delete.title'),
      body: <ConfirmationBody />,
      isConfirmButtonDanger: true,
      onConfirm: () => deleteReport({ id }),
      confirm: 'Delete',
    });
  };

  const menu = useDropdownMenu({ onClickMenuDelete, onClickMenuExport });

  return (
    <div className={classes.cardWrapper} onClick={onClickMenuExport}>
      <div className={classes.content}>
        <div className={classes.titleWrapper}>
          <Typography.Title ellipsis className="mb-0" level={4}>
            {name}
          </Typography.Title>
          {isDataSource ? (
            <Tooltip title="The data from this report is used in the formation of charts and other project statistics">
              <Tag color="green">{moment(metricsDate).format('MMMM YYYY')}</Tag>
            </Tooltip>
          ) : null}
        </div>
        <Typography.Text className="mt-2" type="secondary">
          {moment(new Date(createdAt)).format(DATE_TIME_FORMAT_MOMENT_DOTS)}
        </Typography.Text>
      </div>
      <Dropdown className={classes.dropdownToggler} overlay={menu} trigger={['click']}>
        <Button
          icon={<MoreOutlined className={classes.dropdownTogglerIcon} />}
          type="link"
          onClick={(e) => e.stopPropagation()}
        ></Button>
      </Dropdown>
    </div>
  );
};
